import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MSISDN_HEADER = 'summary';
const ICCID_HEADER = 'custom field (iccid/sc serial number)';

const normalizeHeader = (h) => String(h ?? '').toLowerCase().trim();

const cellToString = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') {
    if ('result' in value) return String(value.result ?? '').trim();
    if ('text' in value) return String(value.text ?? '').trim();
    if (Array.isArray(value.richText)) {
      return value.richText.map((rt) => rt.text ?? '').join('').trim();
    }
  }
  return String(value).trim();
};

export const parseBindingsFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.worksheets[0];
  if (!sheet) {
    throw new Error('Excel file has no sheets');
  }
  if (sheet.rowCount < 2) {
    throw new Error('Excel file is empty');
  }

  const headerRow = sheet.getRow(1);
  const headerIndex = {};
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    headerIndex[normalizeHeader(cellToString(cell.value))] = colNumber;
  });

  const missing = [];
  if (!headerIndex[MSISDN_HEADER]) missing.push('Summary');
  if (!headerIndex[ICCID_HEADER]) missing.push('Custom field (ICCID/SC serial number)');
  if (missing.length > 0) {
    throw new Error(`Excel file is missing required columns: ${missing.join(', ')}`);
  }

  const msisdnCol = headerIndex[MSISDN_HEADER];
  const iccidCol = headerIndex[ICCID_HEADER];

  const bindings = [];
  for (let r = 2; r <= sheet.rowCount; r += 1) {
    const row = sheet.getRow(r);
    const msisdn = cellToString(row.getCell(msisdnCol).value);
    const iccid = cellToString(row.getCell(iccidCol).value);
    if (msisdn || iccid) {
      bindings.push({ msisdn, iccid });
    }
  }
  return bindings;
};

export const downloadResultsExcel = async (results, filename = 'sim-swap-results.xlsx') => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Results');
  sheet.columns = [
    { header: 'msisdn', key: 'msisdn', width: 16 },
    { header: 'iccid', key: 'iccid', width: 24 },
    { header: 'status', key: 'status', width: 14 },
  ];
  results.forEach((r) => {
    sheet.addRow({
      msisdn: r.msisdn,
      iccid: r.iccid,
      status: r.status,
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
};
